import { PayloadAction } from '@reduxjs/toolkit'
import Context from '@store/middleware/context'
import {
  setEngineState,
  HistoryState,
  setIsLoadingScene,
  setHistoryMode,
  setHistorySliderPosition,
  setShowHistoryDialog,
  setProjectFrameSize
} from '@store/slices/historySlice'
import {
  put,
  fork,
  take,
  all,
  call,
  takeEvery,
  select
} from 'redux-saga/effects'
import createEventChannel from '../createEventChannel'
import { Engine } from '@services/engine/engine'
import {
  DeviceStatusEventPayload,
  ModelLoadedStatusEventPayload,
  PropertyListHasChangedData,
  Vec2d
} from '@services/engine/types'
import { PayloadType, RootState } from '@store/store'
import {
  setPropertyState,
  setEngineState as sceneSliceSetEngineState
} from '@store/slices/sceneSlice'
import { initializeFontService } from '../fontSaga/fontSaga'
import { handleRequestFontChannel } from '../document/sceneSaga'
import { EventChannel } from 'redux-saga'
import { Size } from '@components/avatar/Avatar'

function* initializeEngine() {
  if (Context.Engine) {
    yield call(Context.Engine.setRenderOnlyFrameContent, true)
    // unmount engine before initializing
    yield Context.Engine.unmount()
    yield Context.Engine.mount()
    return
  }
  Context.Engine = new Engine()
  yield call(Context.Engine.setRenderOnlyFrameContent, true)
  // no need for Launch Darkly features in community page yet
  const features = '{}'
  yield fork(Context.Engine.initialize, features)
}

function* handleSetEngineState({
  payload
}: PayloadAction<HistoryState['engineState']>) {
  switch (payload) {
    case 'INITIALIZE':
      yield put(initializeFontService())
      yield call(clearEngineCache)
      yield call(initializeEngine)
      yield put(setEngineState('INITIALIZED'))
      break
    case 'INITIALIZED':
      yield all([
        fork(handleRequestFontChannel),
        fork(handleInitializedEngineAndLoadDocument),
        fork(handlePropertyListHasChangedChannel)
      ])
      break
    case 'INITIALIZED_AND_DOCUMENT_LOADED':
      yield put(setHistoryMode('enabled'))

      // Note: This should be called AFTER enabling history mode to prevent the commit from being pushed onto the undo-redo stack (JGAFAR)
      yield put(
        setPropertyState({
          key: 'frameEnabled',
          value: false
        })
      )

      yield put(setIsLoadingScene(false))
      break
    case 'UNMOUNT':
      if (Context.Engine?.setRenderOnlyFrameContent) {
        yield call(Context.Engine.setRenderOnlyFrameContent, false)
      }

      yield put(setShowHistoryDialog({ isOpen: false }))
      yield unmountEngine()
      yield put(setHistoryMode('disabled'))
      setHistorySliderPosition(1)
      yield put(setProjectFrameSize(null))
      break
    case 'UNMOUNTED':
      yield put(setIsLoadingScene(true))
      break
    default:
      break
  }
}

function* handleSceneSliceSetEngineState({
  payload
}: PayloadAction<PayloadType<typeof sceneSliceSetEngineState>>) {
  const { engineState }: HistoryState = yield select(
    (state: RootState) => state.history
  )

  // Syncs engineState of historySlice and sceneSlice when engine unmounts from studio page
  // or when there is an error emitted from EngineModule.ts
  if (
    (payload === 'UNMOUNTED' || payload === 'ERROR') &&
    engineState !== 'UNMOUNTED'
  ) {
    yield put(setEngineState(payload))
  }

  if (payload === 'ERROR') {
    yield put(setShowHistoryDialog({ isOpen: false }))
  }
}

async function clearEngineCache() {
  if (!caches) return

  const keyList = await caches.keys()

  const EngineCacheKeys = ['engine/build/neo.js', 'engine/build/neo.wasm']

  await Promise.all(
    keyList.map(async key => {
      const cache = await caches.open(key)
      const cacheKeys = await cache.keys()

      return Promise.all(
        cacheKeys
          .filter(entry => EngineCacheKeys.some(key => entry.url.includes(key)))
          .map(entry => cache.delete(entry))
      )
    })
  )
}

function* handleInitializedEngineAndLoadDocument() {
  if (Context.Engine?.loadDocument) {
    const { projectUuid } = yield select((state: RootState) => state.project)
    if (!projectUuid) return
    yield fork(
      Context.Engine.loadDocument,
      `${process.env.NEXT_PUBLIC_SERVICE_CORE_API_ENDPOINT}/projects/${projectUuid}/document`
    )
  }

  yield fork(handleStatusChannel)
  yield fork(handleDeviceStatusChannel)
}

function* unmountEngine() {
  if (Context.Engine) {
    yield call(Context.Engine.unmount)

    yield [
      Context.Engine.EngineStatusChannel,
      Context.Engine.EngineDataChannel,
      Context.Engine.EngineDebugChannel,
      Context.Engine.EnginePropertyListHasChangedChannel
    ].forEach(channel => channel.removeAllListeners())
  }

  yield put(setEngineState('UNMOUNTED'))
}

function* handleStatusChannel(): Generator<any, void, any> {
  if (!Context.Engine?.EngineStatusChannel) {
    return
  }
  const statusChannel = yield call(
    createEventChannel,
    Context.Engine.EngineStatusChannel,
    'status'
  )
  while (true) {
    try {
      const statusEvent: ModelLoadedStatusEventPayload =
        yield take(statusChannel)
      const modelLoaded = statusEvent?.status === 1
      if (modelLoaded) {
        yield put(setEngineState('INITIALIZED_AND_DOCUMENT_LOADED'))
      }
    } catch (err) {
      console.error(err)
    }
  }
}

function* handleDeviceStatusChannel(): Generator<any, void, any> {
  if (!Context.Engine?.EngineStatusChannel) {
    return
  }
  const channel = yield call(
    createEventChannel,
    Context.Engine?.EngineStatusChannel,
    'device-status'
  )
  while (true) {
    try {
      const deviceStatus: DeviceStatusEventPayload = yield take(channel)

      if (deviceStatus === DeviceStatusEventPayload.SLOW_INTEL_GPU) {
        // TODO handle slow intel gpu
      }
    } catch (err) {
      console.error(err)
    }
  }
}

function* handleSetHistoryMode({
  payload
}: PayloadAction<HistoryState['historyMode']>) {
  // enabled by default when engine is initialized from community page
  yield Context.Engine?.setHistoryMode(payload === 'enabled' ? 1 : 0)
}

function* handleHistorySliderPosition({
  payload
}: PayloadAction<HistoryState['historySliderPosition']>) {
  // Note: For any given slider position, if the frame were enabled in the project in the edit corresponding to that slider position
  // then the frame will be visible, so we must disable it. (JGAFAR)
  yield put(
    setPropertyState({
      key: 'frameEnabled',
      value: false
    })
  )

  yield Context.Engine?.setHistorySliderPos(payload)
}

function* handlePropertyListHasChangedChannel() {
  if (!Context.Engine) return

  const dataChannel: EventChannel<PropertyListHasChangedData> = yield call(
    createEventChannel,
    Context.Engine?.EnginePropertyListHasChangedChannel,
    Context.Engine?.enginePropertyListHasChangedChannelKey
  )

  try {
    while (true) {
      const dataEvent: PropertyListHasChangedData = yield take(dataChannel)
      const projectFrameSize: Size = yield select(
        (state: RootState) => state.history.projectFrameSize
      )

      // Note: We only want the frame size from when the project first loads as this is the frame size that was last used in that project. In case this listener fires again, we ignore the event if the project frame size is already set. (JGAFAR)
      if (!projectFrameSize) {
        const groups = dataEvent.UI.Groups
        const frameGroup = groups.find(group => group.Label === 'Frame')
        const frameSizeData = frameGroup?.Properties.find(
          property => property.path === 'frame.size'
        )

        const frameSize: Vec2d | null = frameSizeData?.value

        if (frameSize) {
          yield put(
            setProjectFrameSize({ width: frameSize.x, height: frameSize.y })
          )
        } else {
          yield put(setProjectFrameSize({ width: 1080, height: 1080 }))
        }
      }
    }
  } catch (err) {
    console.error(err)
  }
}

function* handleSetShowHistoryDialog({
  payload
}: PayloadAction<HistoryState['showHistoryDialog']>) {
  if (payload.isOpen) {
    yield put(setEngineState('INITIALIZE'))
  }
}

export default function* historyEngineSaga() {
  yield all([
    takeEvery(setEngineState.type, handleSetEngineState),
    takeEvery(sceneSliceSetEngineState.type, handleSceneSliceSetEngineState),
    takeEvery(setHistoryMode.type, handleSetHistoryMode),
    takeEvery(setHistorySliderPosition.type, handleHistorySliderPosition),
    takeEvery(setShowHistoryDialog.type, handleSetShowHistoryDialog)
  ])
}
